import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, TextField, Typography, IconButton, Grid, Backdrop, CircularProgress, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

const FileUpload = ({
    uploadUrl,
    buttonText = "Upload File",
    accept = ".csv",
    onSuccess,
    onError,
    InfoDialogComponent,
    ErrorDialogComponent,
    showFileName = true,
    size = "small"
}) => {
    const [file, setFile] = useState(null);
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorData, setErrorData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const resetFileStateAndHandleError = (errorDataToSet) => {
        // Reset state even on error so user can try again
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        // Call error callback if provided
        if (onError) {
            onError(errorDataToSet);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            alert("Please select a file first.");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        setUploading(true);

        try {
            const response = await fetch(uploadUrl, {
                method: "POST",
                body: formData,
            });

            if (response.ok) {
                let responseData = null;
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.includes("application/json")) {
                    responseData = await response.json();
                }

                // Reset state
                setFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }

                if (onSuccess) {
                    onSuccess(responseData);
                } else {
                    alert("File uploaded successfully!");
                }
            } else {
                const errorData = await response.json();
                const formattedErrorData = {
                    message: errorData.message || "Error uploading file",
                    errors: errorData.errors || []
                };
                setErrorData(formattedErrorData);
                setErrorDialogOpen(true);

                resetFileStateAndHandleError(formattedErrorData);
            }
        } catch (error) {
            console.error("Upload Error:", error);

            const errorDataToSet = { message: "Error uploading file", errors: [] };
            setErrorData(errorDataToSet);
            setErrorDialogOpen(true);
            resetFileStateAndHandleError(errorDataToSet);
        } finally {
            setUploading(false);
        }
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith(".csv")) {
                alert("Invalid file type. Please select a CSV file.");
                setFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            } else {
                setFile(selectedFile);
            }
        }
    };

    return (
        <>
            <Grid container direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                <Grid item sx={{ flexShrink: 0 }}>
                    <TextField
                        type="file"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                        inputRef={fileInputRef}
                        accept={accept}
                        id={`file-input-${uploadUrl}`}
                    />
                    <label htmlFor={`file-input-${uploadUrl}`}>
                        {!file ? (
                            <Button variant="contained" component="span" size={size}>
                                {buttonText}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleUpload}
                                disabled={!file}
                                size={size}
                            >
                                Upload
                            </Button>
                        )}
                    </label>
                </Grid>
                {InfoDialogComponent && (
                    <Grid item sx={{ flexShrink: 0 }}>
                        <IconButton
                            onClick={() => setInfoDialogOpen(true)}
                            size={size}
                            sx={{ color: 'primary.main' }}
                        >
                            <InfoIcon />
                        </IconButton>
                    </Grid>
                )}
                {file && showFileName && (
                    <Grid item>
                        <Typography
                            variant="body2"
                            sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                            {file.name}
                        </Typography>
                    </Grid>
                )}
            </Grid>
            {InfoDialogComponent && (
                <InfoDialogComponent
                    open={infoDialogOpen}
                    onClose={() => setInfoDialogOpen(false)}
                />
            )}
            {ErrorDialogComponent && (
                <ErrorDialogComponent
                    open={errorDialogOpen}
                    onClose={() => setErrorDialogOpen(false)}
                    errorData={errorData}
                />
            )}
            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                open={uploading}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <CircularProgress color="inherit" size={60} />
                    <Typography variant="h6">Uploading file...</Typography>
                </Box>
            </Backdrop>
        </>
    );
};

FileUpload.propTypes = {
    uploadUrl: PropTypes.string.isRequired,
    buttonText: PropTypes.string,
    accept: PropTypes.string,
    onSuccess: PropTypes.func,
    onError: PropTypes.func,
    InfoDialogComponent: PropTypes.elementType,
    ErrorDialogComponent: PropTypes.elementType,
    showFileName: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large'])
};

export default FileUpload;

